import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '@/ui/button/Button';
import { Card } from '@/ui/Card';
import { Input } from '@/ui/Input';
import {
  SETTING_CREDIT_ENFORCEMENT_ENABLED,
  SETTING_DAYTONA_SNAPSHOT_NAME,
  SETTING_EMAIL_FROM_ADDRESS,
  SETTING_LICENSE_MARGIN_PERCENT,
  SETTING_POSTMARK_SERVER_TOKEN,
  sendTestEmail,
  updateSystemSetting,
  useEmailConfig,
  useSystemSettings,
} from '@/api-models/admin-settings';
import { errorText } from '@/features/admin/format';

interface SettingFieldProps {
  /** Value currently stored on the server; the input re-syncs when it changes. */
  value: string;
  placeholder?: string;
  /** `password` clears itself after saving and treats blank as "leave as-is". */
  type?: 'text' | 'password';
  disabled?: boolean;
  successMessage: string;
  onSave: (value: string) => Promise<void>;
}

/**
 * One saved-on-demand settings input. Every field on this page has the same
 * shape — local draft, re-sync on refetch, dirty check, spinner — so it lives
 * here once rather than being retyped per field.
 */
function SettingField({ value, placeholder, type = 'text', disabled, successMessage, onSave }: SettingFieldProps) {
  // A secret is never sent back to us, so the draft starts empty and blank means
  // "keep the current one" rather than "clear it".
  const isSecret = type === 'password';
  const [draft, setDraft] = useState(isSecret ? '' : value);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isSecret) setDraft(value);
  }, [value, isSecret]);

  const trimmed = draft.trim();
  const dirty = isSecret ? trimmed.length > 0 : trimmed !== value;

  async function handleSave() {
    if (!trimmed || isSaving) return;
    setIsSaving(true);
    try {
      await onSave(trimmed);
      if (isSecret) setDraft('');
      toast.success(successMessage);
    } catch (err) {
      toast.error(errorText(err, 'Failed to update'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="flex gap-2">
      <Input
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={isSecret ? 'new-password' : undefined}
        className="flex-1"
      />
      <Button
        type="button"
        variant="primary"
        size={9}
        onClick={handleSave}
        disabled={!dirty || disabled}
        isLoading={isSaving}
      >
        Save
      </Button>
    </div>
  );
}

export function AdminSettingsPage() {
  const { settings, mutate, isLoading } = useSystemSettings(true);
  const { config: emailConfig, mutate: mutateEmail, isLoading: isEmailLoading } = useEmailConfig(true);
  const [isTesting, setIsTesting] = useState(false);

  const currentSnapshot = settings.find((s) => s.key === SETTING_DAYTONA_SNAPSHOT_NAME)?.value ?? '';
  const currentMargin = settings.find((s) => s.key === SETTING_LICENSE_MARGIN_PERCENT)?.value ?? '';
  const enforcementRaw = settings.find((s) => s.key === SETTING_CREDIT_ENFORCEMENT_ENABLED)?.value ?? '';
  const enforcementOn = ['true', '1', 'on', 'yes'].includes(enforcementRaw.trim().toLowerCase());

  async function setEnforcement(next: boolean) {
    try {
      await updateSystemSetting(SETTING_CREDIT_ENFORCEMENT_ENABLED, next ? 'true' : 'false');
      await mutate();
      toast.success(next ? 'Credit enforcement switched on' : 'Credit enforcement switched off');
    } catch (err) {
      toast.error(errorText(err, 'Failed to update enforcement'));
    }
  }

  async function handleSendTest() {
    if (isTesting) return;
    setIsTesting(true);
    try {
      const { sent_to } = await sendTestEmail();
      toast.success(`Test email sent to ${sent_to}`);
    } catch (err) {
      toast.error(errorText(err, 'Failed to send test email'));
    } finally {
      setIsTesting(false);
    }
  }

  const tokenSource =
    emailConfig?.token_source === 'setting'
      ? 'Using the server token saved here.'
      : emailConfig?.token_source === 'env'
        ? 'Using the POSTMARK_API_KEY environment variable. Saving a token here overrides it.'
        : 'No token configured — outbound email is switched off.';

  return (
    <>
      <header>
        <h1 className="page-title">System settings</h1>
        <p className="page-subtitle">
          Runtime configuration for the platform. Changes apply on the next sandbox provision — no redeploy needed.
        </p>
      </header>

      <Card padding="md" radius="2xl" className="flex flex-col gap-3">
        <header>
          <h2 className="card-heading">Daytona snapshot</h2>
          <p className="mt-1 text-xs text-fg-muted">
            Default Daytona snapshot used for new sandboxes. Per-workspace overrides still take precedence. If empty,
            falls back to the <code className="font-mono text-fg-tertiary">SANDBOX_SNAPSHOT_NAME</code> env var.
          </p>
        </header>

        <SettingField
          value={currentSnapshot}
          placeholder="zooid-sb-agent-runtime-1.4.0"
          disabled={isLoading}
          successMessage="Snapshot version updated"
          onSave={async (value) => {
            await updateSystemSetting(SETTING_DAYTONA_SNAPSHOT_NAME, value);
            await mutate();
          }}
        />
      </Card>

      <Card padding="md" radius="2xl" className="flex flex-col gap-5">
        <header>
          <h2 className="card-heading">Billing</h2>
          <p className="mt-1 text-xs text-fg-muted">
            Licence allowances and credit enforcement. There is no payment provider yet &mdash; licences and credits are
            set per space under <span className="text-fg-tertiary">Teams</span>.
          </p>
        </header>

        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-fg-secondary">Enforcement</label>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant={enforcementOn ? 'destructive' : 'primary'}
              disabled={isLoading}
              onClick={() => setEnforcement(!enforcementOn)}
            >
              {enforcementOn ? 'Switch enforcement off' : 'Switch enforcement on'}
            </Button>
            <span className="text-xs text-fg-muted">
              {enforcementOn
                ? 'On — a space with no credit left has its agent work refused and its sandbox stopped.'
                : 'Off — balances are tracked and shown, but nothing is refused.'}
            </span>
          </div>
          <p className="text-xs text-warning">
            Switching this on can stop live agents in every space that is already over its allowance. Check the balances
            on the Teams page first.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-fg-secondary">Licence margin (%)</label>
          <SettingField
            value={currentMargin}
            placeholder="30"
            disabled={isLoading}
            successMessage="Licence margin updated"
            onSave={async (value) => {
              await updateSystemSetting(SETTING_LICENSE_MARGIN_PERCENT, value);
              await mutate();
            }}
          />
          <p className="text-xs text-fg-muted">
            Taken off the licence price to reach the included usage allowance. At 30%, $20/$80/$200 per seat buy
            $14/$56/$140 of usage. Blank falls back to 30.
          </p>
          <p className="text-xs text-warning">
            Applied live: changing this re-prices every space&rsquo;s <em>open</em> period at once and can push spaces
            straight to exhausted. Already-settled periods are untouched.
          </p>
        </div>
      </Card>

      <Card padding="md" radius="2xl" className="flex flex-col gap-5">
        <header>
          <h2 className="card-heading">Email (Postmark)</h2>
          <p className="mt-1 text-xs text-fg-muted">
            Outbound email goes through Postmark. Everything is set up already &mdash; paste a server token and that is
            it. Changes take effect on the next email, with no restart.
          </p>
        </header>

        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-fg-secondary">Server token</label>
          <SettingField
            type="password"
            value=""
            placeholder={emailConfig?.token_masked ?? 'Not configured'}
            disabled={isEmailLoading}
            successMessage="Postmark server token saved"
            onSave={async (value) => {
              await updateSystemSetting(SETTING_POSTMARK_SERVER_TOKEN, value);
              await Promise.all([mutate(), mutateEmail()]);
            }}
          />
          <p className="text-xs text-fg-muted">
            Postmark → your server → <span className="text-fg-tertiary">API Tokens</span>. Stored encrypted and never
            shown again — leave blank to keep the current one. {tokenSource}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-fg-secondary">From address</label>
          <SettingField
            value={emailConfig?.from_address ?? ''}
            placeholder="Zooid <noreply@zooid.app>"
            disabled={isEmailLoading}
            successMessage="Sender address updated"
            onSave={async (value) => {
              await updateSystemSetting(SETTING_EMAIL_FROM_ADDRESS, value);
              await Promise.all([mutate(), mutateEmail()]);
            }}
          />
          <p className="text-xs text-fg-muted">
            Must be a verified sender signature in Postmark, or every send is rejected. Falls back to the{' '}
            <code className="font-mono text-fg-tertiary">FROM_EMAIL</code> env var.
          </p>
        </div>

        <div className="flex items-center gap-3 border-t border-line pt-4">
          <Button type="button" size={9} onClick={handleSendTest} disabled={isEmailLoading} isLoading={isTesting}>
            Send test email
          </Button>
          <p className="text-xs text-fg-muted">Sends a message to your own address using the settings above.</p>
        </div>
      </Card>
    </>
  );
}
